<template>
  <div
    class="flex h-full flex-col hyphens-auto font-serif text-sm md:text-6.5pt"
  >
    <div class="relative py-1.5" :class="{ 'border-b': !top }">
      <nav
        class="flex items-start justify-center gap-4"
        :class="{ 'px-8': closable }"
      >
        <ParagraphLink
          v-if="previous && showLinks"
          class="mt-0.5 flex-none select-none"
          :legalText="legalText.slug"
          :legalTextParagraph="previous"
          :title="$t('legal_text.middleColumn.navigation.previous_paragraph')"
          :prefetch="{ delay: 5000, immediate: true }"
        >
          <i-heroicons-arrow-long-left-20-solid
            class="mx-auto h-4 w-4 text-gray-700"
          />
        </ParagraphLink>
        <component
          :is="isMainContent ? 'h1' : 'h2'"
          class="grow text-center text-6.5pt font-semibold"
          :class="{
            'pl-8': !previous && showLinks,
            'pr-8': !next && showLinks,
          }"
        >
          {{ legalTextParagraph.paragraph }}
          <LawVersionIndicator :legalTextParagraph />
          {{ legalTextParagraph.title }}
        </component>
        <ParagraphLink
          v-if="next && showLinks"
          class="mt-0.5 flex-none select-none"
          :legalText="legalText.slug"
          :legalTextParagraph="next"
          :title="$t('legal_text.middleColumn.navigation.next_paragraph')"
          :prefetch="{ delay: 5000, immediate: true }"
        >
          <i-heroicons-arrow-long-right-20-solid
            class="mx-auto h-4 w-4 text-gray-700"
          />
        </ParagraphLink>
      </nav>
      <div
        v-if="closable"
        class="absolute inset-y-0 right-0 flex items-center justify-center"
      >
        <LocaleLink
          class="rounded-full p-1 text-gray-600 transition hover:bg-gray-200 hover:text-gray-800"
          :href="currentParagraphHref"
          :title="$t('legal_text.middleColumn.navigation.close_old_version')"
        >
          <i-heroicons-x-mark-20-solid aria-hidden="true" class="h-5 w-5" />
          <span class="sr-only">{{
            $t("legal_text.middleColumn.navigation.close_old_version")
          }}</span>
        </LocaleLink>
      </div>
    </div>
    <div
      ref="paragraphWrapper"
      class="paragraph-wrapper space-y-2 overflow-y-auto pb-2 pr-2 pt-4 text-justify lg:pl-2 lg:pr-3"
      @pointerup="$emit('selection', $event.pointerType !== 'mouse')"
      @contextmenu="$emit('selection', false)"
    >
      <ParagraphNode
        v-for="node in nodesWithEntries"
        :key="node.id"
        :node="node"
        :legalText="legalText"
        :legalTextParagraph="legalTextParagraph"
        @delete="$emit('delete', $event)"
        @move="$emit('move', $event)"
        @editLink="$emit('editLink', $event)"
        @contextMenu="openContextMenu"
      />
    </div>
    <ContextMenu
      :items="contextEntries"
      :open="contextEntries.length > 0"
      :legalText="legalText"
      :legalTextParagraph="legalTextParagraph"
      @close="contextEntries = []"
      @delete="$emit('delete', $event)"
    />
  </div>
</template>

<script lang="ts" setup>
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";
import type {
  ILinkChain,
  INote,
  IParagraphEntry,
  ISchema,
} from "~/models/IUserEntry";
import type { ILegalText } from "~/models/ILegalText";

const props = defineProps<{
  legalText: ILegalText;
  legalTextParagraph: ILegalTextParagraph;
  isMainContent?: boolean;
  showLinks?: boolean;
  closable?: boolean;
}>();

defineEmits<{
  delete: [entry: IParagraphEntry];
  move: [entry: ILinkChain | INote | ISchema];
  editLink: [entry: ILinkChain];
  selection: [touch: boolean];
}>();

const { legalTextParagraph, legalText } = toRefs(props);
const legalTextSlug = computed(() => legalText.value.slug);
const paragraphSlug = computed(() => legalTextParagraph.value.slug);

const paragraphId = computed(() => legalTextParagraph.value.id);
const { data: entries } = useFetchParagraphEntries(paragraphId);

const { next, previous } = useToc(legalTextSlug, paragraphSlug);

const { nodesWithEntries } = useNodesWithEntries(legalTextParagraph, entries);

const paragraphWrapper = ref<HTMLElement>();

const { arrivedState } = useScroll(paragraphWrapper);

const { top } = toRefs(arrivedState);

const { href: currentParagraphHref } = useOldParagraphLink();

const contextEntries = ref<Array<IParagraphEntry>>([]);

const openContextMenu = (entries: IParagraphEntry[]) => {
  contextEntries.value = entries.filter(
    (entry: IParagraphEntry) => entry.editable
  );
};
</script>

<style lang="postcss">
.paragraph-wrapper {
  @apply leading-9;

  ol {
    @apply leading-9;

    &.has-order {
      @apply list-none;

      li {
        @apply grid grid-cols-[auto,1fr] gap-4;
      }
    }

    &:not(.has-order) {
      @apply list-decimal;
    }

    li {
      @apply py-1;

      > * {
        @apply inline;
      }
    }
  }

  dt {
    @apply float-left;
  }

  dd {
    @apply ml-6;
  }

  i {
    @apply mx-1 italic;
  }

  table {
    @apply table-auto leading-normal;

    td,
    th {
      @apply p-1;
    }

    &.bordered {
      @apply border border-gray-600;

      tr {
        @apply border-b border-gray-600;
      }

      td,
      th {
        @apply border-x border-gray-600 p-1;
      }
    }
  }

  sup {
    @apply ml-1 mr-0.5;
  }
}
</style>
